/**
 * TierGate — decide si una ruta se monta, ANTES de montarla.
 *
 * Una vista que se monta y después descubre que el usuario no debía verla ya
 * disparó sus queries (y con ellas el `ErrorBox`). Aquí la vista hija solo se
 * monta cuando la sesión, el `/me` y el permiso están resueltos a favor.
 *
 *   MSAL arrancando o procesando el redirect  → verificando
 *   sin sesión                                → login (mismo que `/`, sin
 *                                               redirigir: la URL se conserva)
 *   token en curso                            → verificando
 *   token o `/me` con error                   → error de sesión, no "sin acceso"
 *   `/me` todavía sin datos                   → verificando
 *   sin permiso (`allow` → false)             → `denied`, o NoAccessView
 *   con permiso                               → la vista
 *
 * `allow` recibe el `/me` completo, no solo el tier: hay permisos que no son
 * de tier (`is_platform_admin`). Los predicados viven en `lib/tiers.ts`.
 *
 * `/me` pendiente se decide por la ausencia de datos y de error, no por
 * `isLoading`: en TanStack Query v5 `isLoading` es `isPending && isFetching`,
 * y hay renders con token disponible donde la query está pendiente pero
 * todavía no marcó el fetch.
 *
 * No sustituye al backend: las lecturas siguen protegidas allí. Esto decide
 * qué se muestra.
 */

import type { ReactNode } from 'react'
import { InteractionStatus } from '@azure/msal-browser'
import { useIsAuthenticated, useMsal } from '@azure/msal-react'
import { useTranslation } from 'react-i18next'

import type { MeResponse } from '../api/me'
import { ScrollPage } from '../components/shell/ScrollPage'
import { NoAccessView } from '../routes/NoAccessView'
import { isMsalConfigured } from './msalConfig'
import { LoginScreen } from './LoginScreen'
import { MeError, MsalNotConfigured } from './SessionScreens'
import { useAccessToken } from './useAccessToken'
import { useMe } from './useMe'

interface TierGateProps {
  allow: (me: MeResponse) => boolean
  /** Lo que se muestra sin permiso. Por defecto, `NoAccessView`. */
  denied?: ReactNode
  children: ReactNode
}

export function TierGate({ allow, denied, children }: TierGateProps) {
  if (!isMsalConfigured) return <MsalNotConfigured />
  return (
    <SessionTierGate allow={allow} denied={denied}>
      {children}
    </SessionTierGate>
  )
}

function SessionTierGate({ allow, denied, children }: TierGateProps) {
  const { inProgress } = useMsal()
  const isAuthenticated = useIsAuthenticated()
  const { accessToken, loading: tokenLoading, error: tokenError } = useAccessToken()
  const { me, error: meError } = useMe()

  if (!isAuthenticated) {
    // Mismo criterio que `UnauthenticatedTemplate`: mientras MSAL arranca o
    // procesa el redirect todavía no sabe si hay sesión.
    if (
      inProgress === InteractionStatus.Startup ||
      inProgress === InteractionStatus.HandleRedirect
    ) {
      return <AccessChecking />
    }
    return <LoginScreen />
  }
  if (tokenError) return <SessionError error={tokenError} />
  if (tokenLoading || !accessToken) return <AccessChecking />
  if (meError) return <SessionError error={meError} />
  if (!me) return <AccessChecking />
  if (!allow(me)) return <>{denied ?? <NoAccessView />}</>
  return <>{children}</>
}

function AccessChecking() {
  const { t } = useTranslation('common')
  return (
    <ScrollPage>
      <p role="status" className="font-mono text-sm text-ink-subtle">
        {t('access_check.loading')}
      </p>
    </ScrollPage>
  )
}

function SessionError({ error }: { error: unknown }) {
  return (
    <ScrollPage>
      <MeError error={error} />
    </ScrollPage>
  )
}
